
import React, { useState, useMemo } from 'react';
import { AppState, Staff, Shift } from '../types';
import { LOCATIONS } from '../constants';
import { buildRotaWithAI, getShiftExplanation } from '../services/geminiService';

interface RotaPlannerProps {
  state: AppState;
  staff: Staff[];
  shifts: Shift[];
  setShifts: React.Dispatch<React.SetStateAction<Shift[]>>;
  onFullScreenToggle: () => void;
  isFullScreen: boolean;
}

const ROTA_START_DATE = new Date('2024-10-21T00:00:00');

const RotaPlanner: React.FC<RotaPlannerProps> = ({ state, staff, shifts, setShifts, onFullScreenToggle, isFullScreen }) => {
  const [isBuilding, setIsBuilding] = useState(false);
  const [aiResponse, setAiResponse] = useState<any>(null);
  const [selectedShiftId, setSelectedShiftId] = useState<string | null>(null);
  const [explanation, setExplanation] = useState<string | null>(null);
  const [mobileView, setMobileView] = useState<'table' | 'list'>('table');
  const localLocationId = state.currentLocation;
  
  const [isAddingShift, setIsAddingShift] = useState(false);
  const [newShiftData, setNewShiftData] = useState<{
    staffId: string;
    dayIndex: number;
    role: string;
    startTime: string;
    endTime: string;
  } | null>(null);

  const selectedLocation = useMemo(() => 
    LOCATIONS.find(l => l.id === localLocationId) || LOCATIONS[0],
    [localLocationId]
  );

  const filteredStaff = useMemo(() => 
    staff.filter(s => s.assignedLocationIds.includes(localLocationId)),
    [staff, localLocationId]
  );

  const handleBuildAI = async () => {
    setIsBuilding(true);
    try {
      const result = await buildRotaWithAI(
        filteredStaff,
        shifts.filter(s => s.locationId === localLocationId),
        state.currentDepartment,
        "Maximum 37.5 hours per person. Must meet site opening hours. Prioritise assigned staff skills."
      );
      const updatedShifts = shifts.map(s => {
        const assignment = result.assignments.find((a: any) => a.shiftId === s.id);
        return assignment ? { ...s, staffId: assignment.staffId } : s;
      });
      setShifts(updatedShifts);
      setAiResponse(result);
    } catch (err) {
      alert("Scheduling system error. Please check your connection.");
    } finally {
      setIsBuilding(false);
    }
  };

  const handleExplain = async (shift: Shift) => {
    if (!shift.staffId) return;
    setSelectedShiftId(shift.id);
    setExplanation("Retrieving assignment reasoning...");
    const staffMember = staff.find(s => s.id === shift.staffId);
    if (staffMember) {
      const text = await getShiftExplanation(shift, staffMember);
      setExplanation(text);
    }
  };

  const openAddShiftModal = (staffId: string, dayIndex: number) => {
    setNewShiftData({ staffId, dayIndex, role: 'Support Worker', startTime: '09:00', endTime: '17:00' });
    setIsAddingShift(true);
  };

  const handleSaveManualShift = () => {
    if (!newShiftData) return;
    const shiftDate = new Date(ROTA_START_DATE);
    shiftDate.setDate(ROTA_START_DATE.getDate() + newShiftData.dayIndex);
    const dateStr = shiftDate.toISOString().split('T')[0];
    const newShift: Shift = {
      id: `manual-${Math.random().toString(36).substr(2, 9)}`,
      staffId: newShiftData.staffId,
      locationId: localLocationId,
      department: state.currentDepartment,
      startTime: `${dateStr}T${newShiftData.startTime}:00`,
      endTime: `${dateStr}T${newShiftData.endTime}:00`,
      roleName: newShiftData.role,
      requiredSkills: [],
      status: 'Published'
    };
    setShifts(prev => [...prev, newShift]);
    setIsAddingShift(false);
  };

  const daysLabels = ['MON 21', 'TUE 22', 'WED 23', 'THU 24', 'FRI 25', 'SAT 26', 'SUN 27'];

  return (
    <div className={`space-y-6 md:space-y-8 pb-10 ${isFullScreen ? 'bg-white min-h-screen p-4 md:p-12' : ''} transition-all duration-500`}>
      <div className="flex flex-col xl:flex-row justify-between items-start xl:items-center border-b-2 md:border-b-4 border-black pb-6 md:pb-8 gap-6">
        <div className="flex-1 w-full md:w-auto">
          <div className="space-y-1 mb-2">
            <span className="inline-block bg-[#CCE1FF] px-1 text-[8px] md:text-[10px] font-black text-gray-500 uppercase tracking-[0.2em]">Operational Site</span>
            <div className="flex items-center justify-between">
              <h1 className="ymca-brand-font text-4xl md:text-6xl tracking-tighter uppercase leading-none">{selectedLocation.name}</h1>
              <button onClick={onFullScreenToggle} className="lg:hidden p-2 border border-black hover:bg-black hover:text-white transition-all">
                {isFullScreen ? (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="square" strokeWidth={2} d="M4 10V4m0 0h6m-6 0l7 7m10 3v6m0 0h-6m6 0l-7-7" /></svg>
                ) : (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="square" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5v-4m0 4h-4m4 0l-5-5" /></svg>
                )}
              </button>
            </div>
          </div>
          <p className="text-gray-500 font-bold uppercase text-[10px] md:text-xs tracking-[0.2em]">{state.currentDepartment} Division • {selectedLocation.type}</p>
        </div>
        
        <div className="flex flex-wrap gap-2 md:gap-4 w-full xl:w-auto">
          <button 
            onClick={handleBuildAI} 
            disabled={isBuilding}
            className="flex-1 xl:flex-none flex items-center justify-center space-x-3 px-6 py-4 bg-black text-white hover:bg-ymca-orange transition-all disabled:opacity-50"
          >
            <span className={`w-2 h-2 ${isBuilding ? 'animate-ping' : ''} bg-white`}></span>
            <span className="font-black text-[10px] tracking-widest uppercase">{isBuilding ? 'Running AI Opt...' : 'AI Site Rota'}</span>
          </button>
          <button onClick={onFullScreenToggle} className="hidden xl:flex px-6 py-4 border-2 border-black hover:bg-black hover:text-white transition-all font-black text-[10px] tracking-widest uppercase">
            {isFullScreen ? 'Exit Focus Mode' : 'Focus Mode'}
          </button>
          <button className="flex-1 xl:flex-none px-6 py-4 bg-white border-2 border-black font-black text-[10px] tracking-widest uppercase hover:bg-black hover:text-white transition-all">Publish</button>
        </div>
      </div>

      <div className="bg-white border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] lg:shadow-[12px_12px_0px_0px_rgba(0,0,0,1)] overflow-hidden">
        {/* Responsive Table Wrapper */}
        <div className="overflow-x-auto overflow-y-hidden scroll-smooth scrollbar-hide">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-black text-white">
                <th className="p-4 md:p-6 text-left ymca-brand-font text-2xl tracking-widest min-w-[180px] md:w-64 border-r border-white/10 sticky left-0 bg-black z-30">STAFF</th>
                {daysLabels.map(day => (
                  <th key={day} className="p-4 md:p-6 text-center ymca-brand-font text-xl md:text-2xl tracking-widest border-r border-white/10 min-w-[180px] md:min-w-[240px]">
                    {day}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-black/10">
              {filteredStaff.map(person => (
                <tr key={person.id} className="hover:bg-gray-50 group">
                  <td className="p-4 md:p-6 border-r border-black/10 sticky left-0 bg-white group-hover:bg-gray-50 z-20 shadow-[2px_0_5px_rgba(0,0,0,0.05)] md:shadow-none">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 md:w-10 md:h-10 bg-black text-white flex items-center justify-center font-bold text-xs md:text-base">
                        {person.firstName[0]}{person.lastName[0]}
                      </div>
                      <div className="min-w-0">
                        <p className="font-black text-[10px] md:text-xs tracking-tight uppercase leading-none mb-1 truncate">{person.firstName} {person.lastName}</p>
                        <p className="text-[8px] md:text-[9px] text-gray-400 font-bold uppercase tracking-widest truncate">{person.contractType}</p>
                      </div>
                    </div>
                  </td>
                  {daysLabels.map((dayLabel, i) => {
                    const colDate = new Date(ROTA_START_DATE);
                    colDate.setDate(ROTA_START_DATE.getDate() + i);
                    const colDateStr = colDate.toISOString().split('T')[0];
                    const dayShifts = shifts.filter(s => s.staffId === person.id && s.locationId === localLocationId && s.startTime.startsWith(colDateStr));

                    return (
                      <td key={dayLabel} className="p-2 md:p-4 border-r border-black/10 align-top h-32 md:h-44 group-hover:bg-blue-50/10 transition-colors">
                        <div className="flex flex-col gap-2 h-full">
                          {dayShifts.map(shift => (
                            <div 
                              key={shift.id} 
                              onClick={() => handleExplain(shift)}
                              className="p-3 md:p-4 bg-white border-2 border-black flex flex-col justify-between cursor-pointer hover:border-ymca-orange hover:bg-ymca-orange/5 transition-all shadow-sm group/shift"
                            >
                              <div>
                                <div className="flex justify-between items-start mb-1">
                                  <span className="text-[7px] md:text-[9px] font-black text-gray-400 uppercase tracking-widest group-hover/shift:text-black">{shift.roleName}</span>
                                </div>
                                <p className="ymca-brand-font text-base md:text-xl leading-none">
                                  {new Date(shift.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} — {new Date(shift.endTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                </p>
                              </div>
                              <div className="text-[6px] md:text-[7px] font-black text-black/30 text-right uppercase mt-2">Detail</div>
                            </div>
                          ))}
                          <div 
                            onClick={() => openAddShiftModal(person.id, i)}
                            className="flex-1 border border-dashed border-gray-200 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-all cursor-pointer hover:bg-black/5 hover:border-black"
                          >
                            <span className="text-black text-xl">+</span>
                          </div>
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="lg:hidden flex flex-col items-center justify-center p-8 bg-black text-white text-center space-y-4">
        <svg className="w-8 h-8 animate-bounce text-ymca-orange" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="square" strokeWidth={2} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" /></svg>
        <p className="ymca-brand-font text-2xl uppercase tracking-widest">Landscape Recommended</p>
        <p className="text-[10px] font-medium opacity-60 uppercase tracking-widest">Rotate your device for the best planning experience.</p>
      </div>

      {/* Manual Shift Modal */}
      {isAddingShift && newShiftData && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[100] p-4 animate-in fade-in duration-200" onClick={() => setIsAddingShift(false)}>
          <div className="bg-white max-w-md w-full p-8 border-4 border-black shadow-2xl space-y-8" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-start">
              <h3 className="ymca-brand-font text-5xl tracking-tighter uppercase leading-none">Assign Shift</h3>
              <button onClick={() => setIsAddingShift(false)} className="text-black hover:text-ymca-orange text-4xl leading-none">&times;</button>
            </div>
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4 text-[10px] font-black text-gray-500 uppercase tracking-widest border-b border-black/10 pb-4">
                <div>Staff: <span className="text-black">{staff.find(s => s.id === newShiftData.staffId)?.firstName}</span></div>
                <div>Day: <span className="text-black">{daysLabels[newShiftData.dayIndex]}</span></div>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] block mb-2">Service Role</label>
                  <select 
                    value={newShiftData.role}
                    onChange={(e) => setNewShiftData({...newShiftData, role: e.target.value})}
                    className="w-full p-4 border-2 border-black font-bold uppercase text-xs"
                  >
                    <option>Support Worker</option>
                    <option>Night Support</option>
                    <option>Duty Manager</option>
                    <option>Activity Worker</option>
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-2">Start</label>
                    <input type="time" value={newShiftData.startTime} onChange={(e) => setNewShiftData({...newShiftData, startTime: e.target.value})} className="w-full p-4 border-2 border-black font-bold" />
                  </div>
                  <div>
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-2">End</label>
                    <input type="time" value={newShiftData.endTime} onChange={(e) => setNewShiftData({...newShiftData, endTime: e.target.value})} className="w-full p-4 border-2 border-black font-bold" />
                  </div>
                </div>
              </div>
            </div>
            <div className="pt-4 flex flex-col gap-3">
              <button onClick={handleSaveManualShift} className="w-full py-5 bg-black text-white font-black text-xs tracking-widest uppercase hover:bg-ymca-orange">Save Site Shift</button>
              <button onClick={() => setIsAddingShift(false)} className="w-full py-5 border-2 border-black font-black text-xs tracking-widest uppercase hover:bg-gray-100">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* AI Explanation Modal */}
      {selectedShiftId && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[100] p-4" onClick={() => setSelectedShiftId(null)}>
          <div className="bg-white max-w-2xl w-full p-8 md:p-12 border-4 border-black animate-in zoom-in duration-300" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-start mb-8">
              <h3 className="ymca-brand-font text-5xl md:text-7xl tracking-tighter uppercase">AI reasoning</h3>
              <button onClick={() => setSelectedShiftId(null)} className="text-black hover:text-ymca-orange text-4xl leading-none">&times;</button>
            </div>
            <div className="p-6 md:p-10 bg-ymca-neutral border-l-8 border-black font-medium text-lg md:text-2xl text-gray-800 leading-relaxed font-serif italic">
              {explanation}
            </div>
            <div className="pt-10">
              <button onClick={() => setSelectedShiftId(null)} className="w-full md:w-auto px-12 py-5 bg-black text-white text-[10px] font-black tracking-[0.2em] uppercase hover:bg-gray-900 transition-all">Close Detail</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RotaPlanner;
